import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, UntypedFormControl} from "@angular/forms";
import {Observable, Subject, takeUntil} from "rxjs";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {TableColumn} from "@vex/interfaces/table-column.interface";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {NotificationsService} from "@core/services/backend/common/service/notifications.service";
import {fadeInUp400ms} from "@vex/animations/fade-in-up.animation";
import {stagger40ms} from "@vex/animations/stagger.animation";
import {UsersService} from "@core/services/backend/common/service/users.service";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {map, startWith} from "rxjs/operators";
import {NotificationDetailDialogComponent} from './notification-detail-dialog.component';

export interface Notification {
    ID?: number;
    id?: number;
    user_id?: number;
    userId?: number;
    type?: string;
    Type?: string;
    message?: string;
    Message?: string;
    read_it?: boolean;
    readIt?: boolean;
    ReadIt?: boolean;
    aux_id?: number;
    auxId?: number;
    AuxID?: number;
    priority?: string;
    Priority?: string;
    category?: string;
    Category?: string;
    action_url?: string;
    actionUrl?: string;
    ActionURL?: string;
    expires_at?: string;
    expiresAt?: string;
    ExpiresAt?: string;
    created_at?: string;
    createdAt?: string;
    CreatedAt?: string;
    updated_at?: string;
    updatedAt?: string;
    UpdatedAt?: string;
    userName?: string;
}

@Component({
    selector: 'vex-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    animations: [
        fadeInUp400ms,
        stagger40ms
    ]
})
export class NotificationsComponent implements OnInit {

    layoutCtrl = new UntypedFormControl('boxed');
    private readonly destroying$ = new Subject<void>();

    notificationsList: Notification[] = [];
    dataSource: MatTableDataSource<Notification> | null;
    searchForm: FormGroup;
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    columns: TableColumn<Notification>[] = [
        {label: 'GRIDS.ID', property: 'id', type: 'text', visible: false},
        {label: 'Nombre', property: 'userName', type: 'text', visible: true, cssClasses: ['font-medium']},
        {label: 'NOTIFICATIONS.MENSAJE', property: 'message', type: 'text', visible: true},
        {label: 'NOTIFICATIONS.LEIDA', property: 'read_it', type: 'badge', visible: true},
        {label: 'NOTIFICATIONS.FECHA', property: 'created_at', type: 'text', visible: true},
        {label: 'GRIDS.ACCIONES', property: 'actions', type: 'button', visible: true}
    ];

    currentPage = 0;
    pageSize = 20;
    pageSizeOptions: number[] = [10, 20, 50, 100];
    isLoading: boolean = false;

    // Filtros
    readStatusOptions = [
        {value: null, label: 'TODAS'},
        {value: false, label: 'NO_LEIDAS'},
        {value: true, label: 'LEIDAS'}
    ];

    typeOptions: string[] = [];
    priorityOptions = [
        {value: '', label: 'TODAS'},
        {value: 'low', label: 'BAJA'},
        {value: 'normal', label: 'NORMAL'},
        {value: 'high', label: 'ALTA'},
        {value: 'urgent', label: 'URGENTE'}
    ];

    usersList: any[] = [];
    filteredUsersList: Observable<any[]>;

    constructor(
        private notificationsService: NotificationsService,
        private usersService: UsersService,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private snackbar: MatSnackBar
    ) {
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource();
        this.searchForm = new FormGroup({
            message: new FormControl(),
            userId: new FormControl(),
            readIt: new FormControl(null),
            type: new FormControl(),
            priority: new FormControl(''),
            category: new FormControl()
        });
        this.loadUsers();
        this.loadData();
    }

    loadUsers() {
        this.usersService.list('', 0, 10000, '', false)
            .pipe(takeUntil(this.destroying$))
            .subscribe({
                next: (data: any) => {
                    this.usersList = data.data || data || [];
                    // Configurar autocomplete
                    this.filteredUsersList = this.searchForm.get('userId')!.valueChanges.pipe(
                        startWith(''),
                        map(value => {
                            const searchTerm = typeof value === 'string' ? value : this.getUserDisplayName(value);
                            return searchTerm ? this._filterUsers(searchTerm) : this.usersList.slice();
                        }),
                    );
                },
                error: (err) => {
                    console.error('Error loading users:', err);
                }
            });
    }

    private _filterUsers(searchTerm: string): any[] {
        const filterValue = searchTerm.toLowerCase();
        return this.usersList.filter(user => {
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
            const email = (user.email || '').toLowerCase();
            return fullName.indexOf(filterValue) !== -1 || email.indexOf(filterValue) !== -1;
        });
    }

    displayUserFn(user: any): string {
        if (user) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '';
        }
        return '';
    }

    getUserDisplayName(user: any): string {
        if (!user) return '';
        if (typeof user === 'string') return user;
        return this.displayUserFn(user);
    }

    onUserSelectionChange(event: MatAutocompleteSelectedEvent) {
        // El usuario ya está seleccionado en el formControl
    }

    getUserName(userId: number): string {
        const user = this.usersList.find(u => (u.ID || u.id) === userId);
        if (user) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '';
        }
        return `Usuario ${userId}`;
    }

    loadData() {
        const that = this;
        this.isLoading = true;
        this.initializeData().then(() => {
            // El nombre de usuario ya viene del backend en user_name
            // Solo agregar si no viene del backend (fallback)
            that.notificationsList = that.notificationsList.map(notif => ({
                ...notif,
                userName: notif.userName || that.getUserName(notif.user_id || notif.userId || 0)
            }));
            this.dataSource.data = that.notificationsList;
            this.isLoading = false;
        });
    }

    private getSearch() {
        const search: any = {};
        
        const userValue = this.searchForm.get("userId")?.value;
        if (userValue) {
            // Si es un objeto usuario, obtener el ID
            if (typeof userValue === 'object' && userValue !== null) {
                search.userId = userValue.ID || userValue.id;
            } else {
                search.userId = userValue;
            }
        }
        
        if (this.searchForm.get("readIt")?.value !== null && this.searchForm.get("readIt")?.value !== undefined) {
            search.readIt = this.searchForm.get("readIt")?.value;
        }
        
        if (this.searchForm.get("type")?.value) {
            search.type = this.searchForm.get("type")?.value;
        }
        
        if (this.searchForm.get("priority")?.value) {
            search.priority = this.searchForm.get("priority")?.value;
        }
        
        if (this.searchForm.get("category")?.value) {
            search.category = this.searchForm.get("category")?.value;
        }

        return search;
    }

    async initializeData() {
        const that = this;
        return await new Promise<void>((resolve, reject) => {
            const searchParams = that.getSearch();
            const searchString = Object.keys(searchParams).length > 0 ? JSON.stringify(searchParams) : '';
            
            const list = this.notificationsService.list(searchString, that.currentPage, that.pageSize, 'id DESC');
            list.pipe(takeUntil(this.destroying$)).subscribe({
                next: (data: any) => {
                    const notifications = data.data || data || [];
                    // Filtrar notificaciones con estado "Creado" o que tengan algún campo relacionado
                    // Buscar en múltiples campos: status, estado, type, category
                    const filteredNotifications = notifications.filter((n: any) => {
                        const status = (n.status || n.Status || n.estado || n.Estado || '').toString().toUpperCase();
                        const type = (n.type || n.Type || '').toString().toUpperCase();
                        const category = (n.category || n.Category || '').toString().toUpperCase();
                        // Excluir notificaciones con estado/tipo/categoría "Creado"
                        return status !== 'CREADO' && 
                               type !== 'CREADO' &&
                               category !== 'CREADO';
                    });
                    
                    that.notificationsList = filteredNotifications.map((n: any) => ({
                        id: n.ID || n.id,
                        user_id: n.user_id || n.UserId || n.userId,
                        type: n.type || n.Type,
                        message: n.message || n.Message,
                        read_it: n.read_it !== undefined ? n.read_it : (n.ReadIt !== undefined ? n.ReadIt : n.readIt),
                        aux_id: n.aux_id || n.AuxID || n.auxId,
                        priority: n.priority || n.Priority || 'normal',
                        category: n.category || n.Category,
                        action_url: n.action_url || n.ActionURL || n.actionUrl,
                        expires_at: n.expires_at || n.ExpiresAt || n.expiresAt,
                        created_at: n.created_at || n.CreatedAt || n.createdAt,
                        updated_at: n.updated_at || n.UpdatedAt || n.updatedAt,
                        userName: n.user_name || n.userName || that.getUserName(n.user_id || n.UserId || n.userId || 0)
                    }));
                    
                    // Extraer tipos únicos para el filtro
                    const types = new Set(notifications.map((n: any) => n.type || n.Type).filter(Boolean));
                    that.typeOptions = Array.from(types) as string[];
                    
                    that.paginator.pageIndex = that.currentPage;
                    that.paginator.length = data.total_records || data.totalRecords || that.notificationsList.length;
                    resolve();
                },
                error: (err) => {
                    console.error('Error loading notifications:', err);
                    that.notificationsList = [];
                    resolve();
                }
            });
        });
    }

    pageChanged(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex;
        this.loadData();
    }

    trackByProperty<T>(index: number, column: TableColumn<T>) {
        return column.property;
    }

    get visibleColumns() {
        return this.columns.filter(column => column.visible).map(column => column.property);
    }

    buscar() {
        this.currentPage = 0;
        this.loadData();
    }

    limpiarFiltros() {
        this.searchForm.reset({
            message: null,
            userId: null,
            readIt: null,
            type: null,
            priority: '',
            category: null
        });
        this.currentPage = 0;
        this.loadData();
    }

    markAsRead(notification: Notification) {
        const that = this;
        const id = notification.id || notification.ID;
        if (!id) return;

        this.notificationsService.update([{
            id: id,
            read_it: true
        }]).subscribe({
            next(result) {
                that.snackbar.open('Notificación marcada como leída', '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                that.snackbar.open('Error al marcar la notificación', '', {
                    duration: 3000
                });
            }
        });
    }

    markAsUnread(notification: Notification) {
        const that = this;
        const id = notification.id || notification.ID;
        if (!id) return;

        this.notificationsService.update([{
            id: id,
            read_it: false
        }]).subscribe({
            next(result) {
                that.snackbar.open('Notificación marcada como no leída', '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                that.snackbar.open('Error al actualizar la notificación', '', {
                    duration: 3000
                });
            }
        });
    }

    markAllAsRead() {
        const that = this;
        // Obtener todas las notificaciones no leídas de la lista actual
        const unreadNotifications = this.notificationsList.filter(n => !n.read_it);
        
        if (unreadNotifications.length === 0) {
            this.snackbar.open('No hay notificaciones sin leer', '', {
                duration: 3000
            });
            return;
        }

        // Crear array de actualizaciones
        const updates = unreadNotifications.map(n => ({
            id: n.id || n.ID,
            read_it: true
        }));

        this.isLoading = true;
        this.notificationsService.update(updates).subscribe({
            next(result) {
                that.snackbar.open(`${unreadNotifications.length} notificación(es) marcada(s) como leída(s)`, '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                that.isLoading = false;
                that.snackbar.open('Error al marcar las notificaciones como leídas', '', {
                    duration: 3000
                });
            }
        });
    }

    deleteNotification(notification: Notification) {
        const that = this;
        this.notificationsService.delete(notification).subscribe({
            next(result) {
                that.snackbar.open('Notificación eliminada correctamente', '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                that.snackbar.open('Error al eliminar la notificación', '', {
                    duration: 3000
                });
            }
        });
    }

    formatDate(dateString: string | undefined): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-CL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getDisplayMessage(notification: Notification): string {
        // El backend envía el mensaje como "WORKED_HOURS_ACCEPTED" o "WORKED_HOURS_COMMENTED"
        const type = (notification.type || notification.Type || '').toUpperCase();
        const message = (notification.message || notification.Message || '').toUpperCase();
        
        // Verificar si es una notificación de horas trabajadas
        if (type === 'WORKED_HOURS' || type.includes('WORKED_HOURS')) {
            if (message === 'WORKED_HOURS_ACCEPTED' || message.includes('WORKED_HOURS_ACCEPTED')) {
                return this.translateService.instant('USUARIO.WORKED_HOURS_ACCEPTED');
            }
            if (message === 'WORKED_HOURS_COMMENTED' || message.includes('WORKED_HOURS_COMMENTED')) {
                return this.translateService.instant('USUARIO.WORKED_HOURS_COMMENTED');
            }
        }
        
        // Si no coincide con ningún patrón, mostrar el mensaje original
        return notification.message || notification.Message || '';
    }

    showNotificationDetail(notification: Notification) {
        const dialogRef = this.dialog.open(NotificationDetailDialogComponent, {
            width: '600px',
            maxWidth: '90vw',
            data: notification,
            panelClass: 'notification-detail-dialog'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result === 'markAsRead' && !notification.read_it) {
                this.markAsRead(notification);
            } else if (result === 'markAsUnread' && notification.read_it) {
                this.markAsUnread(notification);
            } else if (result === 'delete') {
                this.deleteNotification(notification);
            } else if (result) {
                // Si se actualizó la notificación, recargar datos
                this.loadData();
            }
        });
    }
}

